import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, SearchIcon, CodeIcon, MapPinIcon, DatabaseIcon, SparklesIcon, UserSearchIcon, MailIcon, MegaphoneIcon, ShoppingCartIcon, ArrowRightIcon, BookOpenIcon, FileTextIcon, HelpCircleIcon, BotIcon } from 'lucide-react';
import { Button } from './ui/Button';
const featurePages = [{
  name: 'Local Business API',
  description: 'Search and access 200M+ local businesses programmatically.',
  href: '#/local-business-api',
  icon: CodeIcon
}, {
  name: 'Google Maps Scraper',
  description: 'Extract business data from Google Maps at scale.',
  href: '#/google-maps-scraper',
  icon: MapPinIcon
}, {
  name: 'Local Business Enrichment',
  description: 'Enrich your lists with verified emails, phones & more.',
  href: '#/local-business-enrichment',
  icon: SparklesIcon
}, {
  name: 'Owner Finder',
  description: 'Find business owners and decision-makers.',
  href: '#/owner-finder',
  icon: UserSearchIcon
}, {
  name: 'Email Sequencing',
  description: 'Automate outreach to local business leads.',
  href: '#/email-sequencing',
  icon: MailIcon
}, {
  name: 'Openclaw for sales',
  description: 'AI agent that finds, scores, and drafts outreach automatically.',
  href: '#/openclaw-for-sales',
  icon: BotIcon
}];
const solutionsPages = [{
  name: 'Local Marketing',
  description: 'Build targeted local marketing campaigns.',
  href: '#/local-marketing',
  icon: MegaphoneIcon
}, {
  name: 'E-commerce Database',
  description: 'Access verified e-commerce business data.',
  href: '#/ecommerce-database',
  icon: ShoppingCartIcon
}];
const resourcesPages = [{
  name: 'Documentation',
  description: 'API docs and integration guides.',
  href: 'https://docs.openmart.com',
  icon: BookOpenIcon
}, {
  name: 'Blog',
  description: 'Insights on local business data and outreach.',
  href: '#/blog',
  icon: FileTextIcon
}, {
  name: 'Help Center',
  description: 'Get support and answers to common questions.',
  href: 'https://openmart.com/help',
  icon: HelpCircleIcon
}];
const databaseCategories: Record<string, {
  name: string;
  href: string;
}[]> = {
  Services: [{
    name: 'Plumbers',
    href: '#/data'
  }, {
    name: 'Electricians',
    href: '#/data'
  }, {
    name: 'HVAC',
    href: '#/data'
  }, {
    name: 'Landscaping',
    href: '#/data'
  }],
  'Food & Dining': [{
    name: 'Restaurants',
    href: '#/data'
  }, {
    name: 'Cafes',
    href: '#/data'
  }, {
    name: 'Bars',
    href: '#/data'
  }, {
    name: 'Bakeries',
    href: '#/data'
  }],
  Professional: [{
    name: 'Accountants',
    href: '#/data/accounting'
  }, {
    name: 'Law Firms',
    href: '#/data'
  }, {
    name: 'Real Estate',
    href: '#/data'
  }, {
    name: 'Insurance',
    href: '#/data'
  }],
  'Health & Beauty': [{
    name: 'Dentists',
    href: '#/data'
  }, {
    name: 'Hair Salons',
    href: '#/data'
  }, {
    name: 'Gyms',
    href: '#/data'
  }, {
    name: 'Spas',
    href: '#/data'
  }]
};
export function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dbDropdownOpen, setDbDropdownOpen] = useState(false);
  const [solutionsDropdownOpen, setSolutionsDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dbTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const solutionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resourcesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeAllDropdowns = () => {
    setDropdownOpen(false);
    setDbDropdownOpen(false);
    setSolutionsDropdownOpen(false);
    setResourcesDropdownOpen(false);
  };
  const handleFeaturesEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    closeAllDropdowns();
    setDropdownOpen(true);
  };
  const handleFeaturesLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };
  const handleDbEnter = () => {
    if (dbTimeoutRef.current) clearTimeout(dbTimeoutRef.current);
    closeAllDropdowns();
    setDbDropdownOpen(true);
  };
  const handleDbLeave = () => {
    dbTimeoutRef.current = setTimeout(() => setDbDropdownOpen(false), 150);
  };
  const handleSolutionsEnter = () => {
    if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
    closeAllDropdowns();
    setSolutionsDropdownOpen(true);
  };
  const handleSolutionsLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => setSolutionsDropdownOpen(false), 150);
  };
  const handleResourcesEnter = () => {
    if (resourcesTimeoutRef.current) clearTimeout(resourcesTimeoutRef.current);
    closeAllDropdowns();
    setResourcesDropdownOpen(true);
  };
  const handleResourcesLeave = () => {
    resourcesTimeoutRef.current = setTimeout(() => setResourcesDropdownOpen(false), 150);
  };
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (dbTimeoutRef.current) clearTimeout(dbTimeoutRef.current);
      if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
      if (resourcesTimeoutRef.current) clearTimeout(resourcesTimeoutRef.current);
    };
  }, []);
  return <nav className="w-full bg-white py-4 relative z-50 border-b border-slate-100" data-id="element-5199">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5200">
        <div className="flex justify-between items-center h-12" data-id="element-5201">
          {/* Logo */}
          <a href="#/" className="flex-shrink-0 flex items-center gap-2 cursor-pointer" data-id="element-5202">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" data-id="element-5203">
              <div className="w-3 h-3 border-2 border-white rounded-full border-t-transparent transform rotate-45" data-id="element-5204"></div>
            </div>
            <span className="font-bold text-2xl tracking-tight text-slate-900" data-id="element-5205">
              openmart
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-5206">
            {/* Features Dropdown */}
            <div className="relative h-full flex items-center" onMouseEnter={handleFeaturesEnter} onMouseLeave={handleFeaturesLeave} data-id="element-5207">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${dropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              if (dropdownOpen) {
                closeAllDropdowns();
              } else {
                closeAllDropdowns();
                setDropdownOpen(true);
              }
            }} data-id="element-5208">
                Features
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-5209" />
              </button>

              <AnimatePresence data-id="element-5210">
                {dropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-0 pt-4 w-[380px]" data-id="element-5211">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-5212">
                      <div className="p-3 space-y-1" data-id="element-5213">
                        {featurePages.map(page => <a key={page.href} href={page.href} className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors group" onClick={() => setDropdownOpen(false)} data-id="element-5214">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:text-blue-600 group-hover:shadow-sm transition-all flex-shrink-0" data-id="element-5215">
                              <page.icon className="w-5 h-5" data-id="element-5216" />
                            </div>
                            <div data-id="element-5217">
                              <div className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-blue-600 transition-colors" data-id="element-5218">
                                {page.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-5219">
                                {page.description}
                              </div>
                            </div>
                          </a>)}
                      </div>
                      <div className="bg-slate-50 p-4 border-t border-slate-100" data-id="element-5220">
                        <a href="#/features" className="flex items-center justify-between text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors group" onClick={() => setDropdownOpen(false)} data-id="element-5221">
                          View all features
                          <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5222" />
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <a href="#/pricing" className="text-slate-600 hover:text-slate-900 font-medium transition-colors py-2" data-id="element-5223">
              Pricing
            </a>

            {/* Databases Mega-Menu */}
            <div className="relative h-full flex items-center" onMouseEnter={handleDbEnter} onMouseLeave={handleDbLeave} data-id="element-5224">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${dbDropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              if (dbDropdownOpen) {
                closeAllDropdowns();
              } else {
                closeAllDropdowns();
                setDbDropdownOpen(true);
              }
            }} data-id="element-5225">
                Database
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${dbDropdownOpen ? 'rotate-180' : ''}`} data-id="element-5226" />
              </button>

              <AnimatePresence data-id="element-5227">
                {dbDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[800px]" data-id="element-5228">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-5229">
                      {/* Top Highlight Row */}
                      <div className="bg-slate-50 p-6 border-b border-slate-100 flex items-center gap-6" data-id="element-5230">
                        <a href="#/data" className="flex-1 bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group" onClick={() => setDbDropdownOpen(false)} data-id="element-5231">
                          <div className="flex items-center gap-3 mb-1" data-id="element-5232">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-5233">
                              <SearchIcon className="w-4 h-4" data-id="element-5234" />
                            </div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-5235">
                              Browse all categories
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 pl-11" data-id="element-5236">
                            Search our complete directory of 300+ industries
                          </div>
                        </a>
                        <a href="#/local-business-database" className="flex-1 bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all group" onClick={() => setDbDropdownOpen(false)} data-id="element-5237">
                          <div className="flex items-center gap-3 mb-1" data-id="element-5238">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-5239">
                              <DatabaseIcon className="w-4 h-4" data-id="element-5240" />
                            </div>
                            <div className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-5241">
                              Local business database
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 pl-11" data-id="element-5242">
                            Access the full 200M+ local business database
                          </div>
                        </a>
                      </div>

                      {/* Columns */}
                      <div className="p-8 grid grid-cols-4 gap-8" data-id="element-5243">
                        {Object.entries(databaseCategories).map(([category, links]) => <div key={category} data-id="element-5244">
                              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5245">
                                {category}
                              </h3>
                              <ul className="space-y-3" data-id="element-5246">
                                {links.map(link => <li key={link.name} data-id="element-5247">
                                    <a href={link.href} className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors block" onClick={() => setDbDropdownOpen(false)} data-id="element-5248">
                                      {link.name}
                                    </a>
                                  </li>)}
                              </ul>
                            </div>)}
                      </div>

                      {/* Bottom Footer */}
                      <div className="bg-slate-50 p-4 border-t border-slate-100 text-center" data-id="element-5249">
                        <a href="#/data" className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors group" onClick={() => setDbDropdownOpen(false)} data-id="element-5250">
                          Browse all 300+ categories
                          <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5251" />
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Solutions Dropdown */}
            <div className="relative h-full flex items-center" onMouseEnter={handleSolutionsEnter} onMouseLeave={handleSolutionsLeave} data-id="element-5252">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${solutionsDropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              if (solutionsDropdownOpen) {
                closeAllDropdowns();
              } else {
                closeAllDropdowns();
                setSolutionsDropdownOpen(true);
              }
            }} data-id="element-5253">
                Solutions
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${solutionsDropdownOpen ? 'rotate-180' : ''}`} data-id="element-5254" />
              </button>

              <AnimatePresence data-id="element-5255">
                {solutionsDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-0 pt-4 w-[320px]" data-id="element-5256">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-5257">
                      <div className="p-3 space-y-1" data-id="element-5258">
                        {solutionsPages.map(page => <a key={page.href} href={page.href} className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors group" onClick={() => setSolutionsDropdownOpen(false)} data-id="element-5259">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:text-blue-600 group-hover:shadow-sm transition-all flex-shrink-0" data-id="element-5260">
                              <page.icon className="w-5 h-5" data-id="element-5261" />
                            </div>
                            <div data-id="element-5262">
                              <div className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-blue-600 transition-colors" data-id="element-5263">
                                {page.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-5264">
                                {page.description}
                              </div>
                            </div>
                          </a>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown */}
            <div className="relative h-full flex items-center" onMouseEnter={handleResourcesEnter} onMouseLeave={handleResourcesLeave} data-id="element-5265">
              <button className={`flex items-center gap-1 font-medium transition-colors py-2 ${resourcesDropdownOpen ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => {
              if (resourcesDropdownOpen) {
                closeAllDropdowns();
              } else {
                closeAllDropdowns();
                setResourcesDropdownOpen(true);
              }
            }} data-id="element-5266">
                Resources
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${resourcesDropdownOpen ? 'rotate-180' : ''}`} data-id="element-5267" />
              </button>

              <AnimatePresence data-id="element-5268">
                {resourcesDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute top-full left-0 pt-4 w-[320px]" data-id="element-5269">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col" data-id="element-5270">
                      <div className="p-3 space-y-1" data-id="element-5271">
                        {resourcesPages.map(page => <a key={page.href} href={page.href} className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors group" onClick={() => setResourcesDropdownOpen(false)} data-id="element-5272">
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 group-hover:bg-white group-hover:text-blue-600 group-hover:shadow-sm transition-all flex-shrink-0" data-id="element-5273">
                              <page.icon className="w-5 h-5" data-id="element-5274" />
                            </div>
                            <div data-id="element-5275">
                              <div className="text-sm font-bold text-slate-900 mb-0.5 group-hover:text-blue-600 transition-colors" data-id="element-5276">
                                {page.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-5277">
                                {page.description}
                              </div>
                            </div>
                          </a>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-5278">
            <Button href="http://app.openmart.com/register" variant="secondary" size="sm" data-id="element-5279">
              Try for free
            </Button>
            <Button href="http://app.openmart.com/login" variant="primary" size="sm" data-id="element-5280">
              Login
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center" data-id="element-5281">
            <button className="text-slate-600 hover:text-slate-900 focus:outline-none p-2" data-id="element-5282">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5283">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-5284" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>;
}